/**
 * Notification Panel Component
 * Displays list of notifications with actions
 */

import { useNotifications } from "@/contexts";
import { Icon } from "@/components/ui/Icon";
import styles from "./NotificationPanel.module.css";

/**
 * Format relative time
 */
function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 1000 / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Ahora mismo";
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return "Ayer";
  return `Hace ${diffDays} días`;
}

/**
 * Get icon for notification type
 */
function getNotificationIcon(type: string): string {
  switch (type) {
    case "service_requested":
      return "bell";
    case "service_started":
      return "clock";
    case "service_completed":
      return "checkCircle";
    case "low_stock":
      return "alertTriangle";
    case "quote_requested":
      return "document";
    case "payment_received":
      return "dollarSign";
    default:
      return "bell";
  }
}

/**
 * Notification Panel Props
 */
interface NotificationPanelProps {
  onClose: () => void;
  onNavigate: (path: string) => void;
}

/**
 * Notification Panel Component
 */
export function NotificationPanel({
  onClose,
  onNavigate,
}: NotificationPanelProps): React.ReactElement {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const handleNotificationClick = (id: string, actionUrl?: string): void => {
    markAsRead(id);
    if (actionUrl) {
      onNavigate(actionUrl);
    }
  };

  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3 className={styles.title}>Notificaciones</h3>
          {unreadCount > 0 && (
            <span className={styles.badge}>{unreadCount}</span>
          )}
        </div>
        <div className={styles.headerRight}>
          {unreadCount > 0 && (
            <button
              className={styles.markAllButton}
              onClick={markAllAsRead}
              title="Marcar todas como leídas"
            >
              <Icon name="check" size={16} />
            </button>
          )}
          <button
            className={styles.closeButton}
            onClick={onClose}
            title="Cerrar"
          >
            <Icon name="xCircle" size={20} />
          </button>
        </div>
      </div>

      {/* Notifications list */}
      <div className={styles.list}>
        {notifications.length === 0 ? (
          <div className={styles.empty}>
            <Icon name="bell" size={48} />
            <p className={styles.emptyText}>No hay notificaciones</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`${styles.notification} ${
                !notification.read ? styles.unread : ""
              } ${styles[`priority-${notification.priority}`]}`}
              onClick={() =>
                handleNotificationClick(notification.id, notification.actionUrl)
              }
            >
              <div className={styles.notificationIcon}>
                <Icon name={getNotificationIcon(notification.type)} size={20} />
              </div>
              <div className={styles.notificationContent}>
                <div className={styles.notificationHeader}>
                  <h4 className={styles.notificationTitle}>
                    {notification.title}
                  </h4>
                  {!notification.read && (
                    <span className={styles.unreadDot}></span>
                  )}
                </div>
                <p className={styles.notificationMessage}>
                  {notification.message}
                </p>
                <span className={styles.notificationTime}>
                  {getRelativeTime(notification.timestamp)}
                </span>
              </div>
              <button
                className={styles.deleteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteNotification(notification.id);
                }}
                title="Eliminar notificación"
              >
                <Icon name="trash" size={16} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
